import { useState } from 'react'
import { Briefcase, Gauge, Check, ArrowRight, Sparkles } from 'lucide-react'
import { useChatStore, type PersonaId } from '@/store/chatStore'
import { cn } from '@/lib/utils'

const PERSONAS: { id: PersonaId; label: string; icon: typeof Briefcase; description: string; color: string }[] = [
  {
    id: 'product_owner',
    label: 'Product Owner',
    icon: Briefcase,
    description: 'Plan features, analyze impact, manage priorities, and guide product direction.',
    color: 'text-blue-500',
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Gauge,
    description: 'Track rollout readiness, ownership, incidents, and operating risks.',
    color: 'text-emerald-500',
  },
]

export default function OnboardingModal() {
  const completeOnboarding = useChatStore(s => s.completeOnboarding)
  const setUserPreferences = useChatStore(s => s.setUserPreferences)
  const setProjectPersona = useChatStore(s => s.setProjectPersona)
  const activeProjectId = useChatStore(s => s.activeProjectId)
  const [selectedPersona, setSelectedPersona] = useState<PersonaId>('product_owner')
  const [step, setStep] = useState<'welcome' | 'persona' | 'done'>('welcome')

  const handleComplete = () => {
    setUserPreferences({ defaultPersona: selectedPersona })
    if (activeProjectId) {
      setProjectPersona(activeProjectId, selectedPersona)
    }
    completeOnboarding()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {step === 'welcome' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              Welcome to Contextful
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-8">
              Your AI-powered workspace for product, operations, and organizational context.
              Let's set up your Contextful view.
            </p>
            <button
              type="button"
              onClick={() => setStep('persona')}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 'persona' && (
          <div className="p-8">
            <h2 className="text-lg font-bold text-foreground mb-1">
              Choose your default view
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Select the perspective that best matches your Contextful work. Technical repository work opens in Forge.
            </p>

            <div className="space-y-3 mb-8">
              {PERSONAS.map(p => {
                const Icon = p.icon
                const isSelected = selectedPersona === p.id
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPersona(p.id)}
                    className={cn(
                      'flex items-start gap-4 w-full p-4 rounded-xl border transition-all text-left',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border hover:border-border hover:bg-muted/30',
                    )}
                  >
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors',
                      isSelected ? 'bg-primary/10' : 'bg-muted/50',
                    )}>
                      <Icon className={cn('h-5 w-5', isSelected ? p.color : 'text-muted-foreground')} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{p.label}</span>
                        {isSelected && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep('welcome')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep('done')}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-lg font-bold text-foreground mb-2">
              You're all set!
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-2">
              Starting as <strong>{PERSONAS.find(p => p.id === selectedPersona)?.label}</strong>.
              Switch perspectives anytime using the persona switcher in the sidebar.
            </p>
            <p className="text-xs text-muted-foreground mb-8">
              Tip: Pin important decisions from chat so product and operations context stays reusable.
            </p>
            <button
              type="button"
              onClick={handleComplete}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Start Working
              <Sparkles className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
